import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Formik } from 'formik';

import { getGameEventTypeLabel } from 'modules/games/domain/constants/game-event-type-labels';
import { GameDetailsTeamMember, GameEventType } from 'modules/games/domain/interfaces/game-details';
import { GameEventDto } from 'modules/games/domain/interfaces/game-event-dto';
import { useCallback, useMemo, useState } from 'react';
import type { MessageDescriptor } from 'shared/i18n';
import { SingleValue } from 'react-select';
import { UDFormDurationInput } from 'ud-form/components/duration-input';
import UDFormSelect from 'ud-form/components/select';
import { SelectOption } from 'ud-form/components/select/component';
import UDButton from 'ud-ui/components/button';
import * as yup from 'yup';
import * as styles from './game-event-form.styles';

const SHOOTOUT_PERIOD_LABEL = msg`Серия пенальти`;

const periodToOption = (period: number, translate: (descriptor: MessageDescriptor) => string) => ({
  label: period === 0 ? translate(SHOOTOUT_PERIOD_LABEL) : `${period}`,
  value: period,
});

const gameEventTypeToOption = (eventType: GameEventType, translate: (descriptor: MessageDescriptor) => string) => {
  const label = getGameEventTypeLabel(eventType);
  return { label: label ? translate(label) : eventType, value: eventType };
};

const playerToOption = (player: GameDetailsTeamMember) => ({
  label: `#${player.number} ${player.lastName} ${player.firstName}`,
  value: player.id,
});

type GameEventFormProps = {
  members: GameDetailsTeamMember[];
  initialValues: Partial<GameEventDto>;
  onSubmit: (values: GameEventDto) => void;
  onDelete?: (id: number) => void;
};

export const GameEventForm = (props: GameEventFormProps) => {
  const { initialValues, members, onSubmit, onDelete } = props;

  const { t } = useLingui();
  const [selectedPeriod, selectPeriod] = useState<number | null>(initialValues?.period ?? null);
  const [selectedType, setSelectedType] = useState<GameEventType | null>(initialValues?.type ?? null);

  const periodOptions = useMemo(() => [1, 2, 0].map((period) => periodToOption(period, t)), [t]);

  const eventTypeOptions = useMemo(() => {
    return Object.values(GameEventType)
      .filter((eventType) => {
        if (selectedPeriod === 0) {
          return [GameEventType.AFTER_MATCH_PENALTY_GOAL, GameEventType.AFTER_MATCH_PENALTY_MISSED].includes(eventType);
        }

        return ![
          GameEventType.SAVE,
          GameEventType.DEFAULT,
          GameEventType.AFTER_MATCH_PENALTY_GOAL,
          GameEventType.AFTER_MATCH_PENALTY_MISSED,
        ].includes(eventType);
      })
      .map((eventType) => gameEventTypeToOption(eventType, t));
  }, [t, selectedPeriod]);

  const playerOptions = useMemo(() => {
    return members.map(playerToOption);
  }, [members]);

  const hasPlayer = useMemo(
    () =>
      !selectedType ||
      ![
        // GameEventType.PENALTY,
        GameEventType.DEFAULT,
        // GameEventType.TIMEOUT,
        // GameEventType.CORNER,
      ].includes(selectedType),
    [selectedType],
  );

  const hasSecondPlayer = useMemo(
    () => selectedType && [GameEventType.PASS_AND_GOAL, GameEventType.REPLACEMENT].includes(selectedType),
    [selectedType],
  );

  // У «паса и гола» порядок полей — часть контракта записи (`userId` = пасующий, `secondaryUserId` =
  // забивающий), поэтому подписи называют роль, а не номер: иначе оператор гадает, кого куда.
  const isPassAndGoal = selectedType === GameEventType.PASS_AND_GOAL;
  const firstPlayerLabel = isPassAndGoal ? t`Пас` : t`Игрок 1`;
  const secondPlayerLabel = isPassAndGoal ? t`Гол` : t`Игрок 2`;

  const firstMember = initialValues.firstPlayerId ? members.find((member) => member.id === initialValues.firstPlayerId) : null;
  const secondMember = initialValues.secondPlayerId ? members.find((member) => member.id === initialValues.secondPlayerId) : null;

  const formInitialValues = {
    id: initialValues.id,
    teamId: initialValues.teamId,
    type: initialValues.type ? gameEventTypeToOption(initialValues.type, t) : null,
    period: initialValues.period === null || initialValues.period === undefined ? null : periodToOption(initialValues.period, t),
    seconds: initialValues.seconds || null,
    firstPlayerId: firstMember ? playerToOption(firstMember) : null,
    secondPlayerId: secondMember ? playerToOption(secondMember) : null,
  };

  const onTypeChange = useCallback(
    (item: SingleValue<SelectOption>) => {
      setSelectedType(item?.value);
    },
    [setSelectedType],
  );

  const handlePeriodChange = useCallback(
    (setFieldValue: (arg0: string, arg1: null) => void) => (item: any) => {
      selectPeriod(item.value);
      setSelectedType(null);
      setFieldValue('type', null);
    },
    [],
  );

  const handleSubmit = useCallback(
    (values: typeof formInitialValues) => {
      const data = {
        id: values.id,
        teamId: values.teamId!,
        type: values.type?.value || null,
        period: values.period?.value || 0,
        seconds: values.seconds || 0,
        firstPlayerId: values.firstPlayerId?.value || null,
        secondPlayerId: values.secondPlayerId?.value || null,
        videoUrl: initialValues.videoUrl,
      };

      onSubmit(data);
    },
    [initialValues.videoUrl, onSubmit],
  );

  const handleDelete = useCallback(() => {
    if (!initialValues.id || !onDelete) {
      return;
    }

    onDelete!(initialValues.id!);
  }, [initialValues.id, onDelete]);

  const validationSchema = yup.object().shape({
    type: yup
      .object()
      .nullable()
      .required(t`Обязательное поле`),
    period: yup
      .object()
      .nullable()
      .required(t`Обязательное поле`),
    seconds: yup
      .string()
      .nullable()
      .when('period', {
        is: (val: { label: string; value: number } | null) => val === null || val.value === 0,
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.required(t`Обязательное поле`),
      }),
    firstPlayerId: yup.object().when('type', {
      is: (type: { label: string; value: GameEventType }) =>
        ![GameEventType.PENALTY, GameEventType.DEFAULT, GameEventType.TIMEOUT, GameEventType.CORNER].includes(type.value),
      then: (schema) => schema.nullable().required(t`Обязательное поле`),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    secondPlayerId: yup
      .object()
      .nullable()
      .when('type', {
        is: (type: { label: string; value: GameEventType }) =>
          [GameEventType.PASS_AND_GOAL, GameEventType.REPLACEMENT].includes(type.value),
        then: (schema) => schema.required(t`Обязательное поле`),
        otherwise: (schema) => schema.optional(),
      }),
  });

  return (
    <Formik
      initialValues={formInitialValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      {(formProps) => {
        return (
          <form onSubmit={formProps.handleSubmit}>
            <input type="hidden" name="teamId" />

            <styles.GameEventFormTimeContainer className="mt-2">
              <div className="period">
                <UDFormSelect
                  name="period"
                  label={t`Период/тайм`}
                  isMulti={false}
                  options={periodOptions}
                  onChange={handlePeriodChange(formProps.setFieldValue)}
                />
              </div>
              {selectedPeriod !== 0 && (
                <div className="time ml-3">
                  <UDFormDurationInput name="seconds" label={t`Время`} value={formProps.values.seconds} />
                </div>
              )}
            </styles.GameEventFormTimeContainer>

            <div className="mt-2">
              <UDFormSelect name="type" label={t`Событие`} isMulti={false} options={eventTypeOptions} onChange={onTypeChange} />
            </div>

            {hasPlayer && (
              <div className="mt-2">
                <UDFormSelect name="firstPlayerId" label={firstPlayerLabel} isMulti={false} options={playerOptions} />
              </div>
            )}

            {hasSecondPlayer && (
              <div className="mt-2">
                <UDFormSelect name="secondPlayerId" label={secondPlayerLabel} isMulti={false} options={playerOptions} />
              </div>
            )}

            <div className="mt-10 flex gap-4">
              {onDelete && formProps.values.id && (
                <UDButton variant="secondary" className="w-full grow" type="button" onClick={handleDelete}>
                  <Trans>Удалить</Trans>
                </UDButton>
              )}
              <UDButton variant="primary" className="w-full grow" type="submit" loading={formProps.isSubmitting}>
                <Trans>Сохранить</Trans>
              </UDButton>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
